import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  FileText, AlertTriangle, CheckCircle2, Clock,
  UserMinus, Heart, RefreshCw, Send, Loader2,
  FileSignature, Users, Calendar, ChevronRight,
  ClipboardList, Phone, Mail, AlertCircle,
  ArrowRight, Download, Eye, Edit, Trash2
} from "lucide-react";

const EHRDischargeRevocationPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('workflows');
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [pendingNOMNC, setPendingNOMNC] = useState([]);
  const [pendingBereavement, setPendingBereavement] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [nomncDialog, setNomncDialog] = useState(false);
  const [summaryDialog, setSummaryDialog] = useState(false);
  const [bereavementDialog, setBereavementDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [workflowsRes, nomncRes, bereavementRes, statsRes] = await Promise.all([
        api.get('/ehr/discharge/workflow/active'),
        api.get('/ehr/discharge/nomnc/pending'),
        api.get('/ehr/discharge/bereavement/pending'),
        api.get('/ehr/discharge/stats')
      ]);
      
      setWorkflows(workflowsRes.data.active_workflows || []);
      setPendingNOMNC(nomncRes.data.pending_nomnc || []);
      setPendingBereavement(bereavementRes.data.pending_handoffs || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      // Demo data
      setWorkflows([
        {
          id: 'dw_001',
          patient_id: 'P001',
          patient_name: 'Margaret Johnson',
          discharge_reason: 'death',
          status: 'bereavement_handoff',
          nomnc_status: 'signed',
          summary_status: 'complete',
          bereavement_status: 'pending_assignment',
          created_at: new Date(Date.now() - 86400000).toISOString(),
          progress: 80
        },
        {
          id: 'dw_002',
          patient_id: 'P002',
          patient_name: 'Robert Williams',
          discharge_reason: 'revocation',
          status: 'nomnc_pending',
          nomnc_status: 'generated',
          summary_status: null,
          bereavement_status: null,
          created_at: new Date().toISOString(),
          progress: 25
        },
        {
          id: 'dw_003',
          patient_id: 'P003',
          patient_name: 'Dorothy Chen',
          discharge_reason: 'transfer',
          status: 'summary_pending',
          nomnc_status: 'signed',
          summary_status: 'draft',
          bereavement_status: null,
          created_at: new Date(Date.now() - 43200000).toISOString(),
          progress: 50
        }
      ]);
      
      setPendingNOMNC([
        {
          id: 'nomnc_001',
          patient_name: 'Robert Williams',
          status: 'generated',
          delivery_deadline: new Date(Date.now() + 86400000).toISOString(),
          discharge_date: new Date(Date.now() + 259200000).toISOString(),
          reason: 'revocation',
          urgency: 'high'
        }
      ]);
      
      setPendingBereavement([
        {
          id: 'bh_001',
          patient_name: 'Margaret Johnson (Deceased)',
          primary_contact: { name: 'John Johnson', relationship: 'Spouse' },
          risk_level: 'high',
          created_at: new Date().toISOString(),
          days_since_death: 2
        }
      ]);
      
      setStats({
        this_month: { total_discharges: 12, by_reason: { death: 8, revocation: 2, transfer: 1, other: 1 } },
        nomnc_compliance: { on_time_delivery_rate: 98.5, signature_rate: 95.2 },
        bereavement: { active_cases: 45, pending_assignment: 3, completed_this_month: 5 },
        average_length_of_stay: { death: 67, revocation: 23, transfer: 45 }
      });
    } finally {
      setLoading(false);
    }
  };

  const initiateDischarge = async (patientId, chartId, reason, date) => {
    try {
      const response = await api.post('/ehr/discharge/workflow/initiate', null, {
        params: { patient_id: patientId, chart_id: chartId, discharge_reason: reason, discharge_date: date }
      });
      toast.success('Discharge workflow initiated');
      loadData();
    } catch (error) {
      toast.error('Failed to initiate discharge');
    }
  };

  const deliverNOMNC = async (nomncId) => {
    try {
      await api.post(`/ehr/discharge/nomnc/${nomncId}/deliver`);
      toast.success('NOMNC delivery recorded');
      loadData();
    } catch (error) {
      toast.error('Failed to record NOMNC delivery');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      initiated: { label: 'Initiated', color: 'bg-gray-100 text-gray-800' },
      nomnc_pending: { label: 'NOMNC Pending', color: 'bg-yellow-100 text-yellow-800' },
      nomnc_delivered: { label: 'NOMNC Delivered', color: 'bg-blue-100 text-blue-800' },
      summary_pending: { label: 'Summary Pending', color: 'bg-orange-100 text-orange-800' },
      summary_complete: { label: 'Summary Complete', color: 'bg-green-100 text-green-800' },
      bereavement_handoff: { label: 'Bereavement Handoff', color: 'bg-purple-100 text-purple-800' },
      complete: { label: 'Complete', color: 'bg-green-100 text-green-800' }
    };
    const c = config[status] || config.initiated;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getReasonBadge = (reason) => {
    const config = {
      death: { label: 'Death', icon: Heart, color: 'bg-red-100 text-red-800' },
      revocation: { label: 'Revocation', icon: UserMinus, color: 'bg-orange-100 text-orange-800' },
      transfer: { label: 'Transfer', icon: ArrowRight, color: 'bg-blue-100 text-blue-800' },
      moved_out_of_area: { label: 'Moved', icon: ArrowRight, color: 'bg-gray-100 text-gray-800' }
    };
    const c = config[reason] || config.death;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-rose-600" />
              Discharge & Revocation
            </h1>
            <p className="text-slate-600 mt-1">
              NOMNC generation, discharge summaries, and bereavement handoff automation
            </p>
          </div>
          <Button onClick={() => navigate('/ehr-compliance')} variant="outline">
            Back to EHR Dashboard
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-white border-l-4 border-l-rose-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">This Month</p>
                    <p className="text-3xl font-bold text-rose-600">{stats.this_month?.total_discharges}</p>
                    <p className="text-xs text-slate-500">Total Discharges</p>
                  </div>
                  <FileText className="w-10 h-10 text-rose-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">NOMNC Compliance</p>
                    <p className="text-3xl font-bold text-green-600">{stats.nomnc_compliance?.on_time_delivery_rate}%</p>
                    <p className="text-xs text-slate-500">On-Time Delivery</p>
                  </div>
                  <CheckCircle2 className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Active Bereavement</p>
                    <p className="text-3xl font-bold text-purple-600">{stats.bereavement?.active_cases}</p>
                    <p className="text-xs text-slate-500">Cases</p>
                  </div>
                  <Heart className="w-10 h-10 text-purple-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Avg Length of Stay</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.average_length_of_stay?.death}</p>
                    <p className="text-xs text-slate-500">Days (Death)</p>
                  </div>
                  <Calendar className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alerts Row */}
        {(pendingNOMNC.length > 0 || pendingBereavement.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pendingNOMNC.length > 0 && (
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    <div>
                      <p className="font-medium text-yellow-800">
                        {pendingNOMNC.length} NOMNC{pendingNOMNC.length > 1 ? 's' : ''} Pending Delivery
                      </p>
                      <p className="text-sm text-yellow-700">
                        Must be delivered 2 days before discharge
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {pendingBereavement.length > 0 && (
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Users className="w-6 h-6 text-purple-600" />
                    <div>
                      <p className="font-medium text-purple-800">
                        {pendingBereavement.length} Bereavement Handoff{pendingBereavement.length > 1 ? 's' : ''} Pending
                      </p>
                      <p className="text-sm text-purple-700">
                        Awaiting coordinator assignment
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 max-w-2xl">
            <TabsTrigger value="workflows" data-testid="workflows-tab">
              <ClipboardList className="w-4 h-4 mr-2" />
              Workflows
            </TabsTrigger>
            <TabsTrigger value="nomnc" data-testid="nomnc-tab">
              <FileSignature className="w-4 h-4 mr-2" />
              NOMNC
            </TabsTrigger>
            <TabsTrigger value="summaries" data-testid="summaries-tab">
              <FileText className="w-4 h-4 mr-2" />
              Summaries
            </TabsTrigger>
            <TabsTrigger value="bereavement" data-testid="bereavement-tab">
              <Heart className="w-4 h-4 mr-2" />
              Bereavement
            </TabsTrigger>
          </TabsList>

          {/* Workflows Tab */}
          <TabsContent value="workflows" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Active Discharge Workflows</span>
                  <Button size="sm" data-testid="initiate-discharge-btn">
                    <UserMinus className="w-4 h-4 mr-2" />
                    Initiate Discharge
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
                  </div>
                ) : workflows.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No active discharge workflows</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {workflows.map((workflow) => (
                      <Card key={workflow.id} className="border" data-testid={`workflow-${workflow.id}`}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <h3 className="font-semibold text-lg">{workflow.patient_name}</h3>
                              {getReasonBadge(workflow.discharge_reason)}
                              {getStatusBadge(workflow.status)}
                            </div>
                            <div className="text-sm text-slate-500">
                              Started: {new Date(workflow.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          
                          <div className="mb-4">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-slate-600">Workflow Progress</span>
                              <span className="font-medium">{workflow.progress}%</span>
                            </div>
                            <Progress value={workflow.progress} className="h-2" />
                          </div>

                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <FileSignature className="w-4 h-4 text-slate-400" />
                              <span>NOMNC: </span>
                              <Badge variant="outline" className="text-xs">
                                {workflow.nomnc_status || 'N/A'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-slate-400" />
                              <span>Summary: </span>
                              <Badge variant="outline" className="text-xs">
                                {workflow.summary_status || 'Pending'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <Heart className="w-4 h-4 text-slate-400" />
                              <span>Bereavement: </span>
                              <Badge variant="outline" className="text-xs">
                                {workflow.bereavement_status || 'N/A'}
                              </Badge>
                            </div>
                          </div>

                          <div className="flex gap-2 mt-4 pt-4 border-t">
                            {workflow.status === 'nomnc_pending' && (
                              <Button size="sm" variant="outline">
                                <Send className="w-4 h-4 mr-1" />
                                Generate NOMNC
                              </Button>
                            )}
                            {workflow.status === 'summary_pending' && (
                              <Button size="sm" variant="outline">
                                <FileText className="w-4 h-4 mr-1" />
                                Complete Summary
                              </Button>
                            )}
                            {workflow.status === 'bereavement_handoff' && (
                              <Button size="sm" variant="outline">
                                <Users className="w-4 h-4 mr-1" />
                                Assign Coordinator
                              </Button>
                            )}
                            <Button size="sm" variant="ghost">
                              <Eye className="w-4 h-4 mr-1" />
                              View Details
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* NOMNC Tab */}
          <TabsContent value="nomnc" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>NOMNC Management</CardTitle>
                <CardDescription>
                  Notice of Medicare Non-Coverage - Required 2 days before discharge
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pendingNOMNC.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
                      <p>All NOMNCs delivered on time</p>
                    </div>
                  ) : (
                    pendingNOMNC.map((nomnc) => (
                      <Card key={nomnc.id} className="border-l-4 border-l-yellow-500" data-testid={`nomnc-${nomnc.id}`}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold">{nomnc.patient_name}</h3>
                              <div className="text-sm text-slate-600 mt-1">
                                <p>Discharge Date: {new Date(nomnc.discharge_date).toLocaleDateString()}</p>
                                <p className="text-yellow-700 font-medium">
                                  Delivery Deadline: {new Date(nomnc.delivery_deadline).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={nomnc.urgency === 'high' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                                {nomnc.urgency === 'high' ? 'Urgent' : 'Pending'}
                              </Badge>
                              <Button size="sm" onClick={() => deliverNOMNC(nomnc.id)} data-testid={`deliver-nomnc-${nomnc.id}`}>
                                <Send className="w-4 h-4 mr-1" />
                                Record Delivery
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                {/* NOMNC Info Card */}
                <Card className="mt-6 bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-blue-800">NOMNC Compliance Requirements</h4>
                        <ul className="text-sm text-blue-700 mt-2 space-y-1">
                          <li>• Must be delivered at least 2 calendar days before discharge</li>
                          <li>• Patient/representative must sign acknowledging receipt</li>
                          <li>• Includes QIO appeal rights information</li>
                          <li>• Document refusal to sign if applicable</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Summaries Tab */}
          <TabsContent value="summaries" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Discharge Summaries</CardTitle>
                <CardDescription>
                  Auto-compiled summaries with all required documentation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {workflows.filter(w => w.summary_status).map((workflow) => (
                    <Card key={workflow.id} className="border" data-testid={`summary-${workflow.id}`}>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold">{workflow.patient_name}</h3>
                          <Badge className={workflow.summary_status === 'complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {workflow.summary_status === 'complete' ? 'Complete' : 'Draft'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mb-4">
                          Reason: {workflow.discharge_reason}
                        </p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button size="sm" variant="outline">
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button size="sm" variant="outline">
                            <Download className="w-4 h-4 mr-1" />
                            Export
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Card className="mt-6 bg-green-50 border-green-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <FileText className="w-6 h-6 text-green-600 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-green-800">Auto-Compiled Summary Sections</h4>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm text-green-700">
                          <div>• Demographics</div>
                          <div>• Diagnoses</div>
                          <div>• Medications</div>
                          <div>• Symptom History</div>
                          <div>• Care Goals</div>
                          <div>• Visit Summary</div>
                          <div>• Family Support</div>
                          <div>• Bereavement Plan</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bereavement Tab */}
          <TabsContent value="bereavement" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Bereavement Coordinator Handoff</CardTitle>
                <CardDescription>
                  13-month support program coordination for families
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pendingBereavement.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
                      <p>All bereavement handoffs assigned</p>
                    </div>
                  ) : (
                    pendingBereavement.map((handoff) => (
                      <Card key={handoff.id} className="border-l-4 border-l-purple-500" data-testid={`bereavement-${handoff.id}`}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold">{handoff.patient_name}</h3>
                              <div className="text-sm text-slate-600 mt-1">
                                <p>Primary Contact: {handoff.primary_contact?.name} ({handoff.primary_contact?.relationship})</p>
                                <p>Days Since Death: {handoff.days_since_death}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={handoff.risk_level === 'high' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                                {handoff.risk_level} risk
                              </Badge>
                              <Button size="sm" data-testid={`assign-coordinator-${handoff.id}`}>
                                <Users className="w-4 h-4 mr-1" />
                                Assign Coordinator
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                {/* 13-Month Timeline */}
                <Card className="mt-6 bg-purple-50 border-purple-200">
                  <CardContent className="pt-6">
                    <h4 className="font-medium text-purple-800 mb-4">13-Month Bereavement Support Timeline</h4>
                    <div className="grid grid-cols-7 gap-2">
                      {[1, 2, 3, 6, 9, 12, 13].map((month) => (
                        <div key={month} className="text-center p-2 bg-white rounded border">
                          <p className="text-xs text-slate-500">Month {month}</p>
                          <p className="text-xs font-medium text-purple-700">
                            {month === 1 ? 'Initial' : month === 6 ? 'Mid-term' : month === 12 ? 'Anniversary' : month === 13 ? 'Completion' : 'Check-in'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EHRDischargeRevocationPage;
